import { Link } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { news } from "@/mocks/news";
import DramaCard from "@/components/feature/DramaCard";
import NewsCard from "@/components/feature/NewsCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import HeroCarousel from "@/pages/home/components/HeroCarousel";

export default function Home() {
  usePageMeta({
    title: "韩剧TV-最新韩剧在线资讯与剧集推荐",
    description:
      "韩剧TV专注提供最新韩剧资讯和剧集推荐，涵盖热门韩剧介绍、剧情解析和观看指南，是韩剧爱好者了解及时动态和精选内容的首选平台。",
    keywords: "韩剧TV,韩剧,韩剧资讯,韩剧推荐,韩剧剧情,韩剧观看,韩剧大全",
  });

  const featuredDramas = dramas.filter((d) => d.featured).slice(0, 4);
  const latestNews = [...news].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 6);
  const hotDramas = [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 8);
  const hotNews = [...news].slice(0, 5);

  return (
    <div className="w-full">
      <HeroCarousel />

      {/* 编辑精选 */}
      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-primary-600">
              <i className="ri-bookmark-3-line text-lg"></i>
              <span className="font-label text-sm font-500">EDITOR'S PICK</span>
            </div>
            <h2 className="mt-2 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
              编辑精选
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              由编辑部逐部看过，精心挑选的本季必追作品。
            </p>
          </div>
          <Link
            to="/category/drama"
            className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm font-500 text-foreground-700 hover:text-primary-600 md:flex cursor-pointer"
          >
            全部剧集 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {featuredDramas.map((drama) => (
            <DramaCard key={drama.id} drama={drama} />
          ))}
        </div>
      </section>

      {/* 最新资讯 + 热门榜 */}
      <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[2fr_1fr]">
          <div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-accent-600">
                  <i className="ri-newspaper-line text-lg"></i>
                  <span className="font-label text-sm font-500">LATEST NEWS</span>
                </div>
                <h2 className="mt-2 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
                  最新资讯
                </h2>
              </div>
              <Link
                to="/category/news"
                className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-500 text-foreground-700 hover:text-primary-600 cursor-pointer"
              >
                更多资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="mt-8 space-y-4">
              {latestNews.map((item) => (
                <NewsCard key={item.id} item={item} variant="list" />
              ))}
            </div>
          </div>

          <aside>
            <div className="sticky top-24 rounded-xl border border-background-200/70 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <i className="ri-fire-line text-lg text-primary-500"></i>
                <h3 className="font-heading text-lg font-700 text-foreground-950">资讯热榜</h3>
              </div>
              <ol className="mt-4 space-y-1">
                {hotNews.map((item, i) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/news/${item.id}`}
                      className="group flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-background-100 cursor-pointer"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-label text-xs font-600 ${
                          i < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="line-clamp-2 text-sm text-foreground-800 group-hover:text-primary-600">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/specials"
                className="mt-4 flex items-center justify-center gap-1 rounded-md bg-secondary-100 py-2.5 text-sm font-500 text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
              >
                <i className="ri-stack-line"></i> 查看主题专题
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* 热门剧集推荐 */}
      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-primary-600">
              <i className="ri-fire-fill text-lg"></i>
              <span className="font-label text-sm font-500">TRENDING NOW</span>
            </div>
            <h2 className="mt-2 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
              热门剧集推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              按站内热度排序，这些是最近大家追得最多的韩剧。
            </p>
          </div>
          <Link
            to="/category/drama"
            className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm font-500 text-foreground-700 hover:text-primary-600 md:flex cursor-pointer"
          >
            剧集大全 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
          {hotDramas.map((drama) => (
            <DramaCard key={drama.id} drama={drama} />
          ))}
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="w-full px-4 pb-16 md:px-8">
        <div className="relative overflow-hidden rounded-2xl bg-secondary-900 px-6 py-12 md:px-12 md:py-16">
          <div className="relative z-10 grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-700 text-background-50 md:text-3xl">
                关于 <span className="text-primary-300">韩剧TV</span>
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-secondary-200 md:text-base">
                韩剧TV 是一个专注韩剧资讯与剧集推荐的内容平台。我们每日整理新剧动态、演员资讯与深度剧情解析，
                并为每一部作品提供清晰的观看指南，帮助你在海量内容中快速找到值得一看的韩剧。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  了解我们
                </Link>
                <Link
                  to="/specials"
                  className="whitespace-nowrap rounded-md border border-secondary-500 px-6 py-3 text-sm font-500 text-secondary-100 transition-colors hover:bg-secondary-800 cursor-pointer"
                >
                  浏览专题合集
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: "12+", label: "精选剧集" },
                { value: "15+", label: "资讯文章" },
                { value: "4", label: "主题专题" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl bg-secondary-800 px-4 py-6 text-center">
                  <p className="font-heading text-2xl font-800 text-primary-300 md:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-secondary-300">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}